import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { FaSearch, FaArrowLeft, FaFileExport, FaTrophy, FaUniversity, FaChartBar, FaExclamationTriangle, FaStar, FaLightbulb, FaRocket, FaArrowRight } from 'react-icons/fa';

import Spinner from '../components/common/Spinner/Spinner';
import { SearchableSelectBase } from '../components/common/SearchableSelect/SearchableSelect';
import RadarChart from '../components/common/RadarChart/RadarChart';
import { INDICADORES, INDICADOR_LABEL, getFaixaMaturidade } from '../constants/redeFederal';
import { buildRedeFederalAverageRadarData } from '../utils/radarUtils';
import './RedeFederal.css';
import InstitutionalSignature from '../components/common/InstitutionalSignature/InstitutionalSignature';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const logo = '/logo_gif.gif';
const pct = (v) => (typeof v === 'number' ? (v * 100).toFixed(1).replace('.', ',') + '%' : '-');

const TABS = [
  { id: 'panorama', label: 'Panorama' },
  { id: 'criticas', label: 'Questões Críticas' },
  { id: 'orientacao', label: 'Orientação' },
];

const RedeFederal = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [questoesAgg, setQuestoesAgg] = useState(null);
  const [questoesMeta, setQuestoesMeta] = useState({}); // codigo -> { enunciado, eixo, subs }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState('panorama');
  const [indicador, setIndicador] = useState('iESGo');
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState('desc');
  const [chartView, setChartView] = useState('top'); // top | bottom | all

  const [buscaQ, setBuscaQ] = useState('');
  const [temaQ, setTemaQ] = useState('Todos');
  const [expandida, setExpandida] = useState(null);

  useEffect(() => {
    document.body.classList.add('diagnostico-publico');
    return () => document.body.classList.remove('diagnostico-publico');
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [idxResp, qResp] = await Promise.all([
          fetch('/rede-federal/index.json'),
          fetch('/rede-federal/questoes.json'),
        ]);
        if (!idxResp.ok) throw new Error('Falha ao carregar dados da Rede Federal.');
        setData(await idxResp.json());
        
        const qData = qResp.ok ? await qResp.json() : null;
        setQuestoesAgg(qData);
        
        const meta = {};
        if (qData && qData.questoes) {
          qData.questoes.forEach((q) => {
            meta[q.codigo] = { 
              enunciado: q.enunciado || '', 
              eixo: q.eixo || 'Outros', 
              subs: Array.isArray(q.subs) ? q.subs : [] 
            };
          });
        }
        setQuestoesMeta(meta);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const ranking = useMemo(() => {
    if (!data) return [];
    const termo = busca.trim().toLowerCase();
    return data.instituicoes
      .filter((i) => !termo || i.sigla.toLowerCase().includes(termo) || (i.nome || '').toLowerCase().includes(termo))
      .map((i) => ({ ...i, valor: i.indicadores[indicador] }))
      .sort((a, b) => {
        const av = a.valor ?? -1, bv = b.valor ?? -1;
        return ordem === 'desc' ? bv - av : av - bv;
      });
  }, [data, indicador, busca, ordem]);

  const mediaRF = data?.mediasRedeFederal?.[indicador] ?? null;
  const radarMediaRF = useMemo(() => (
    buildRedeFederalAverageRadarData({ medias: data?.mediasRedeFederal || {} })
  ), [data]);

  const melhor = useMemo(() => {
    if (!data) return null;
    const c = data.instituicoes.map((i) => ({ sigla: i.sigla, valor: i.indicadores[indicador] }))
      .filter((i) => typeof i.valor === 'number').sort((a, b) => b.valor - a.valor);
    return c[0] || null;
  }, [data, indicador]);

  const chartData = useMemo(() => {
    let base = [...ranking];
    if (chartView === 'top') base = base.slice(0, 15);
    else if (chartView === 'bottom') base = [...ranking].sort((a, b) => (a.valor ?? 999) - (b.valor ?? 999)).slice(0, 15);
    return {
      labels: base.map((i) => i.sigla),
      datasets: [{
        label: INDICADOR_LABEL[indicador],
        data: base.map((i) => (typeof i.valor === 'number' ? i.valor * 100 : 0)),
        backgroundColor: base.map((i) => getFaixaMaturidade((i.valor || 0) * 100).cor),
        borderRadius: 4,
      }],
      _count: base.length,
    };
  }, [ranking, indicador, chartView]);

  // ---- Questões críticas (enriquecidas com metadados) ----
  const questoesEnriquecidas = useMemo(() => {
    if (!questoesAgg) return [];
    return questoesAgg.questoes
      .filter((q) => q.aplicavel)
      .map((q) => {
        const meta = questoesMeta[q.codigo] || {};
        return { ...q, enunciado: meta.enunciado || '(questão não localizada no catálogo)', eixo: meta.eixo || 'Outros', subs: meta.subs || [] };
      });
  }, [questoesAgg, questoesMeta]);

  const temasQ = useMemo(() => {
    const s = new Set(questoesEnriquecidas.map((q) => q.eixo));
    return ['Todos', ...Array.from(s).sort()];
  }, [questoesEnriquecidas]);

  const criticasFiltradas = useMemo(() => {
    const termo = buscaQ.trim().toLowerCase();
    return questoesEnriquecidas
      .filter((q) => (temaQ === 'Todos' || q.eixo === temaQ) && (!termo || q.codigo.includes(termo) || q.enunciado.toLowerCase().includes(termo)))
      .sort((a, b) => a.mediaScore - b.mediaScore);
  }, [questoesEnriquecidas, buscaQ, temaQ]);

  // ---- Orientação ----
  const orientacao = useMemo(() => {
    if (!questoesAgg || !data) return null;
    const aplic = questoesEnriquecidas;
    const maisCriticas = [...aplic].sort((a, b) => a.mediaScore - b.mediaScore).slice(0, 5);

    // Instituições referência: quem mais aparece em 1º lugar nas questões
    const liderancas = {};
    questoesAgg.questoes.forEach((q) => {
      if (q.aplicavel && q.ranking[0]) liderancas[q.ranking[0].sigla] = (liderancas[q.ranking[0].sigla] || 0) + 1;
    });
    const referencias = Object.entries(liderancas).map(([sigla, n]) => {
      const inst = data.instituicoes.find((i) => i.sigla === sigla);
      return { sigla, slug: inst?.slug, vezes: n, iESGo: inst?.indicadores?.iESGo };
    }).sort((a, b) => b.vezes - a.vezes).slice(0, 6);

    // Temas mais frágeis (média de score por eixo)
    const porTema = {};
    aplic.forEach((q) => {
      if (!porTema[q.eixo]) porTema[q.eixo] = { soma: 0, n: 0 };
      porTema[q.eixo].soma += q.mediaScore;
      porTema[q.eixo].n++;
    });
    const temasFragas = Object.entries(porTema).map(([eixo, d]) => ({ eixo, media: d.soma / d.n }))
      .sort((a, b) => a.media - b.media).slice(0, 4);

    return { maisCriticas, referencias, temasFragas };
  }, [questoesAgg, questoesEnriquecidas, data]);

  const exportarCsv = () => {
    const escapeCsv = (val) => {
      if (val === null || val === undefined) return '';
      const text = String(val);
      if (text.includes(',') || text.includes('"') || text.includes('\n') || text.includes('\r')) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };

    const linhas = [['Posição', 'Sigla', 'Instituição', 'Indicador', 'Valor (%)']];
    ranking.forEach((i, idx) => {
      const pos = idx + 1;
      const sigla = i.sigla;
      const nome = i.nome;
      const ind = INDICADOR_LABEL[indicador] || indicador;
      const val = typeof i.valor === 'number' ? (i.valor * 100).toFixed(1).replace('.', ',') : '';
      linhas.push([pos, sigla, nome, ind, val]);
    });
    
    const csv = linhas.map((l) => l.map(escapeCsv).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `rede-federal-${indicador}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <Spinner text="Carregando panorama da Rede Federal..." fullScreen />;
  if (error) {
    return (
      <div className="rf-container">
        <button className="rf-back-btn" onClick={() => navigate('/login')}><FaArrowLeft /> Voltar</button>
        <div className="rf-empty"><p>{error}</p></div>
      </div>
    );
  }

  const renderDistro = (q) => {
    const cores = { 8: '#16a34a', 7: '#84cc16', 6: '#facc15', 5: '#fb923c', 4: '#dc2626' };
    return (
      <div className="rf-distro" title="Distribuição das respostas (8=adota totalmente ... 4=não adota)">
        {[8, 7, 6, 5, 4].map((v) => {
          const n = q.distribuicao[v] || 0;
          const w = q.validos ? (n / q.validos) * 100 : 0;
          return w > 0 ? <span key={v} style={{ width: `${w}%`, background: cores[v] }} title={`Valor ${v}: ${n}`} /> : null;
        })}
      </div>
    );
  };

  return (
    <div className="rf-container">
      <button className="rf-back-btn no-print" onClick={() => navigate('/login')}><FaArrowLeft /> Voltar para Início</button>

      <header className="rf-hero">
        <img src={logo} alt="Mago" className="rf-logo" />
        <div>
          <h1>Rede Federal no Mago</h1>
          <p>Panorama de governança e gestão das {data.totalInstituicoes} instituições da Rede Federal de Educação Profissional, Científica e Tecnológica, com base nos dados públicos do <strong>TCU (iESGo 2024)</strong>.</p>
        </div>
      </header>

      <div className="rf-institutional-signature">
        <InstitutionalSignature variant="showcase" />
      </div>

      <nav className="rf-tabs no-print">
        {TABS.map((t) => (
          <button key={t.id} className={`rf-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)} type="button">{t.label}</button>
        ))}
      </nav>

      {/* ====== PANORAMA ====== */}
      <div style={{ display: activeTab === 'panorama' ? '' : 'none' }}>
        <section className="rf-kpis">
          <div className="rf-kpi-card">
            <span className="rf-kpi-icon"><FaUniversity /></span>
            <span className="rf-kpi-value">{data.totalInstituicoes}</span>
            <span className="rf-kpi-label">Instituições</span>
          </div>
          <div className="rf-kpi-card">
            <span className="rf-kpi-icon"><FaChartBar /></span>
            <span className={`rf-kpi-value ${getFaixaMaturidade((mediaRF || 0) * 100).classe}`}>{pct(mediaRF)}</span>
            <span className="rf-kpi-label">Média {indicador} da Rede</span>
          </div>
          {melhor && (
            <div className="rf-kpi-card">
              <span className="rf-kpi-icon best"><FaTrophy /></span>
              <span className="rf-kpi-value sigla">{melhor.sigla}</span>
              <span className="rf-kpi-label">Maior {indicador} ({pct(melhor.valor)})</span>
            </div>
          )}
        </section>

        <section className="rf-controls">
          <div className="rf-control-item">
            <label>Indicador (TCU)</label>
            <SearchableSelectBase options={INDICADORES.map((i) => ({ id: i.id, label: `${i.sigla} — ${i.label}` }))} value={indicador} onChange={setIndicador} placeholder="Selecione um indicador..." sx={{ width: '100%' }} />
          </div>
          <div className="rf-control-item">
            <label>Buscar instituição</label>
            <div className="rf-search"><FaSearch /><input type="text" placeholder="Sigla ou nome..." value={busca} onChange={(e) => setBusca(e.target.value)} /></div>
          </div>
          <div className="rf-control-item rf-control-actions">
            <button className="rf-btn-outline" onClick={() => setOrdem((o) => (o === 'desc' ? 'asc' : 'desc'))}>Ordenar: {ordem === 'desc' ? 'Maior → Menor' : 'Menor → Maior'}</button>
            <button className="rf-btn-outline" onClick={exportarCsv}><FaFileExport /> Exportar CSV</button>
          </div>
        </section>

        <section className="rf-chart-card">
          <div className="rf-chart-head">
            <h3>Comparativo — {INDICADOR_LABEL[indicador]}</h3>
            <div className="rf-segmented">
              {[{ id: 'top', l: 'Top 15' }, { id: 'bottom', l: 'Piores 15' }, { id: 'all', l: 'Todas' }].map((o) => (
                <button key={o.id} className={chartView === o.id ? 'active' : ''} onClick={() => setChartView(o.id)} type="button">{o.l}</button>
              ))}
            </div>
          </div>
          <div className="rf-chart-container" style={{ height: chartView === 'all' ? 460 : 340 }}>
            <Bar
              data={chartData}
              options={{
                responsive: true, maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: { callbacks: { label: (c) => `${c.parsed.y.toFixed(1).replace('.', ',')}%` } },
                  datalabels: chartData._count > 20 ? { display: false } : { anchor: 'end', align: 'end', color: '#475569', font: { size: 10, weight: '600' }, formatter: (v) => (typeof v === 'number' ? v.toFixed(0) + '%' : '') },
                },
                scales: { y: { beginAtZero: true, max: 100, ticks: { callback: (v) => v + '%' } }, x: { ticks: { maxRotation: 90, minRotation: chartData._count > 20 ? 90 : 0, font: { size: 10 } } } },
              }}
            />
          </div>
        </section>

        <section className="rf-radar-section">
          <RadarChart
            title="Radar consolidado da Rede Federal"
            data={radarMediaRF}
            ariaLabel="Radar com médias consolidadas da Rede Federal por eixo temático"
            tableCaption="Médias consolidadas da Rede Federal por eixo temático"
          />
        </section>

        <section className="rf-table-card">
          <div className="rf-table-scroll">
            <table className="rf-table">
              <thead><tr><th style={{ width: 56 }}>#</th><th style={{ width: 110 }}>Sigla</th><th>Instituição</th><th style={{ width: 140 }}>{indicador}</th><th style={{ width: 150 }}>Maturidade</th></tr></thead>
              <tbody>
                {ranking.map((i, idx) => {
                  const faixa = getFaixaMaturidade((i.valor || 0) * 100);
                  return (
                    <tr key={i.slug} className="rf-row" onClick={() => navigate(`/rede-federal/${i.slug}`)}>
                      <td className="rf-pos">{idx + 1}</td>
                      <td className="rf-sigla">{i.sigla}</td>
                      <td>{i.nome}</td>
                      <td className="rf-valor">{pct(i.valor)}</td>
                      <td><span className={`rf-badge ${faixa.classe}`}>{faixa.texto}</span></td>
                    </tr>
                  );
                })}
                {ranking.length === 0 && <tr><td colSpan="5" className="rf-empty-row">Nenhuma instituição encontrada.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>

        <div className="rf-quicklinks">
          {data.instituicoes.map((i) => <Link key={i.slug} to={`/rede-federal/${i.slug}`} className="rf-chip">{i.sigla}</Link>)}
        </div>
      </div>

      {/* ====== QUESTÕES CRÍTICAS ====== */}
      <div style={{ display: activeTab === 'criticas' ? '' : 'none' }}>
        <div className="rf-section-intro">
          <FaExclamationTriangle className="rf-intro-icon warn" />
          <p>Onde a Rede Federal está <strong>menos madura</strong>, por questão do iESGo. Ordenado da menor para a maior adoção. Para cada questão, mostramos a <strong>instituição-referência</strong> (melhor desempenho na Rede).</p>
        </div>
        {!questoesAgg ? (
          <div className="rf-empty"><p>Dados por questão indisponíveis.</p></div>
        ) : (
          <>
            <section className="rf-controls">
              <div className="rf-control-item">
                <label>Buscar questão</label>
                <div className="rf-search"><FaSearch /><input type="text" placeholder="Código ou texto da questão..." value={buscaQ} onChange={(e) => setBuscaQ(e.target.value)} /></div>
              </div>
              <div className="rf-control-item">
                <label>Filtrar por tema</label>
                <SearchableSelectBase options={temasQ.map((t) => ({ id: t, label: t }))} value={temaQ} onChange={setTemaQ} placeholder="Tema..." sx={{ width: '100%' }} />
              </div>
            </section>
            <section className="rf-crit-list">
              {criticasFiltradas.map((q, idx) => {
                const ref = q.ranking[0];
                const aberta = expandida === q.codigo;
                return (
                  <article key={q.codigo} className="rf-crit-item">
                    <div className="rf-crit-head" onClick={() => setExpandida(aberta ? null : q.codigo)}>
                      <span className="rf-crit-rank">#{idx + 1}</span>
                      <div className="rf-crit-main">
                        <div className="rf-crit-meta">
                          <span className="rf-resp-codigo">{q.codigo}</span>
                          <span className="rf-resp-tema">{q.eixo}</span>
                        </div>
                        <p className="rf-crit-enunciado">{q.enunciado}</p>
                        {renderDistro(q)}
                      </div>
                      <div className="rf-crit-side">
                        <span className="rf-crit-pct">{(q.pctAdota * 100).toFixed(0)}%</span>
                        <span className="rf-crit-pct-label">adoção</span>
                        {ref && <span className="rf-crit-ref" title="Instituição-referência nesta questão"><FaStar /> {ref.sigla}</span>}
                      </div>
                    </div>
                    {aberta && (
                      <div className="rf-crit-body">
                        <h4>Benchmarking na Rede — quem está melhor nesta questão</h4>
                        <div className="rf-bench-grid">
                          {q.ranking.slice(0, 10).map((r, ri) => (
                            <Link key={r.slug} to={`/rede-federal/${r.slug}`} className="rf-bench-chip" title={`Valor ${r.valor} · ${r.itens} práticas`}>
                              <span className="rf-bench-pos">{ri + 1}</span>{r.sigla}
                              <span className={`rf-bench-dot v${r.valor}`} />
                            </Link>
                          ))}
                        </div>
                        <p className="rf-crit-note">{q.naoAplica > 0 && `${q.naoAplica} instituição(ões) marcaram "não se aplica". `}Escala: valor 8 (adota totalmente) → 4 (não adota).</p>
                      </div>
                    )}
                  </article>
                );
              })}
              {criticasFiltradas.length === 0 && <div className="rf-empty"><p>Nenhuma questão corresponde ao filtro.</p></div>}
            </section>
          </>
        )}
      </div>

      {/* ====== ORIENTAÇÃO ====== */}
      <div style={{ display: activeTab === 'orientacao' ? '' : 'none' }}>
        {!orientacao ? (
          <div className="rf-empty"><p>Dados de orientação indisponíveis.</p></div>
        ) : (
          <div className="rf-orient-grid">
            <section className="rf-orient-card">
              <h3><FaExclamationTriangle className="warn" /> Onde a Rede Federal mais precisa evoluir</h3>
              <p className="rf-orient-sub">Temas com menor maturidade média na Rede:</p>
              <ul className="rf-orient-list">
                {orientacao.temasFragas.map((t) => (
                  <li key={t.eixo}><span>{t.eixo}</span><span className={`rf-badge ${getFaixaMaturidade(t.media * 100).classe}`}>{pct(t.media)}</span></li>
                ))}
              </ul>
              <p className="rf-orient-sub">Questões mais críticas:</p>
              <ul className="rf-orient-list">
                {orientacao.maisCriticas.map((q) => (
                  <li key={q.codigo}><span><strong>{q.codigo}</strong> — {q.enunciado.slice(0, 70)}{q.enunciado.length > 70 ? '…' : ''}</span><span className="rf-mini-pct">{(q.pctAdota * 100).toFixed(0)}%</span></li>
                ))}
              </ul>
              <button className="rf-link-btn" onClick={() => setActiveTab('criticas')}>Ver todas as questões críticas <FaArrowRight /></button>
            </section>

            <section className="rf-orient-card">
              <h3><FaStar className="gold" /> Instituições referência</h3>
              <p className="rf-orient-sub">As que mais aparecem em 1º lugar entre as questões da Rede:</p>
              <ul className="rf-orient-list">
                {orientacao.referencias.map((r) => (
                  <li key={r.sigla}>
                    <Link to={r.slug ? `/rede-federal/${r.slug}` : '#'} className="rf-ref-name">{r.sigla}</Link>
                    <span className="rf-ref-badge">{r.vezes}× referência · iESGo {pct(r.iESGo)}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="rf-orient-card">
              <h3><FaLightbulb className="gold" /> Como usar estes dados no Mago</h3>
              <ul className="rf-orient-bullets">
                <li>Identifique no painel as questões em que sua instituição está abaixo da média da Rede.</li>
                <li>Use a <strong>instituição-referência</strong> de cada questão como benchmark — veja as práticas e evidências dela na página dedicada.</li>
                <li>Priorize no Mago os temas mais frágeis da Rede para ganho coletivo.</li>
                <li>Acompanhe a evolução cadastrando sua instituição e respondendo o diagnóstico vivo.</li>
              </ul>
            </section>

            <section className="rf-orient-card">
              <h3><FaRocket className="blue" /> Próximos passos para a instituição</h3>
              <ol className="rf-orient-steps">
                <li>Abra a página da sua instituição e compare cada indicador com a média da Rede.</li>
                <li>Selecione 3 a 5 questões críticas prioritárias para o próximo ciclo.</li>
                <li>Estude as evidências das instituições-referência nessas questões.</li>
                <li>Registre o plano de ação no Mago e monitore o avanço.</li>
              </ol>
              <button className="rf-link-btn" onClick={() => navigate('/login')}>Entrar no Mago <FaArrowRight /></button>
            </section>
          </div>
        )}
      </div>

      <p className="rf-footnote">Fonte: dados abertos do TCU (iESGo 2024). Clique em uma instituição para ver a página dedicada com indicadores, respostas originais, justificativas e evidências.</p>
    </div>
  );
};

export default RedeFederal;
